import { motion } from "framer-motion";
import { Check, Minus, Star } from "lucide-react";
import { CARE_PLANS, CARE_MATRIX } from "../data/content";
import { SectionHeading, Reveal, GradientButton, GhostButton } from "./primitives";

function PlanCard({ plan, index }) {
  const popular = plan.popular;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: index * 0.07 }}
      whileHover={{ y: -6 }}
      data-testid={`care-plan-${plan.id}`}
      className={[
        "relative flex h-full flex-col rounded-3xl border p-7",
        popular ? "border-brand/40 bg-white/[0.05]" : "border-white/10 bg-white/[0.03]",
      ].join(" ")}
    >
      {popular && (
        <span className="absolute right-5 top-5 inline-flex items-center gap-1 rounded-full bg-brand-gradient px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
          <Star className="h-2.5 w-2.5 fill-white" /> Popular
        </span>
      )}
      <h3 className="font-heading text-lg font-semibold text-white">{plan.name}</h3>
      <div className="mt-3 flex items-end gap-1">
        <span className={popular ? "font-heading text-3xl font-bold text-gradient" : "font-heading text-3xl font-bold text-white"}>{plan.price}</span>
        {plan.per && <span className="mb-1 text-sm text-muted-foreground">{plan.per}</span>}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{plan.tagline}</p>
      <ul className="mt-6 flex-1 space-y-2.5">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

export default function CarePlans() {
  const planNames = Object.keys(CARE_MATRIX.plans);
  return (
    <section data-testid="care-plans" className="bg-ink-soft/40 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          align="center"
          eyebrow="Hosting & Care Plans"
          title="Own it, or let us manage it"
          sub="After launch you can take full ownership of your website, or choose Nova SecureNet for worry-free ongoing management."
          className="mb-14"
        />

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARE_PLANS.map((p, i) => (
            <PlanCard key={p.id} plan={p} index={i} />
          ))}
        </div>

        {/* Comparison table */}
        <Reveal className="mt-16">
          <div className="overflow-hidden rounded-3xl border border-white/10">
            <div className="overflow-x-auto">
              <table data-testid="care-comparison-table" className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03]">
                    <th className="px-6 py-5 text-sm font-semibold text-white">Feature</th>
                    {planNames.map((n) => (
                      <th key={n} className="px-4 py-5 text-center text-sm font-semibold text-white">{n}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {CARE_MATRIX.features.map((feat, r) => (
                    <tr key={feat} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                      <td className="px-6 py-4 text-sm text-white/85">{feat}</td>
                      {planNames.map((n) => (
                        <td key={n} className="px-4 py-4 text-center">
                          {CARE_MATRIX.plans[n][r] ? (
                            <Check className="mx-auto h-5 w-5 text-brand" />
                          ) : (
                            <Minus className="mx-auto h-4 w-4 text-white/20" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 flex flex-col justify-center gap-3 sm:flex-row">
          <GradientButton to="/contact" data-testid="care-cta-book">Choose a Care Plan</GradientButton>
          <GhostButton to="/contact" data-testid="care-cta-ask">Ask a Question</GhostButton>
        </div>
      </div>
    </section>
  );
}
